/**
 * @file Provides the resolved auth session and the provider's components.
 *
 * The provider's `useSession` hook is called exactly ONCE, here, in a real
 * component — everything downstream reads a plain context value. That keeps the
 * rules-of-hooks contract obvious: no consumer is invoking a hook indirectly
 * through an object property, where a conditional render could silently change
 * the call order.
 */
import { createContext, useContext, type ComponentType, type ReactNode } from "react";
import type {
  WebAuthActions,
  WebAuthProvider,
  WebProfileActions,
  WebSecurityActions,
  WebSession,
} from "./types";

interface AuthContextValue {
  readonly session: WebSession;
  /** Password reset and verification-email calls (see WebAuthActions). */
  readonly actions: WebAuthActions;
  /** The active provider's signed-out screen. */
  readonly AuthScreen: ComponentType;
  /** The active provider's header control. */
  readonly UserMenu: ComponentType;
  /** The active provider's password and session actions. */
  readonly security: WebSecurityActions;
  /** The active provider's name and email edits. */
  readonly profile: WebProfileActions;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthRoot({
  provider,
  children,
}: {
  readonly provider: WebAuthProvider;
  readonly children: ReactNode;
}) {
  const session = provider.useSession();

  return (
    <AuthContext.Provider
      value={{
        session,
        actions: provider,
        AuthScreen: provider.AuthScreen,
        UserMenu: provider.UserMenu,
        security: provider.security,
        profile: provider.profile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/** The whole auth context. Throws outside <AuthRoot>, rather than guessing. */
export function useAuthValue(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error("useAuthValue() must be called inside <AuthRoot>.");
  }
  return value;
}

/** Convenience for the common case of only needing session state. */
export function useWebSession(): WebSession {
  return useAuthValue().session;
}
